import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import { styled, useScrollTrigger } from "@mui/material";
import { motion } from "framer-motion";
import ArrowLeft from "@icons/myicons/arrow-left.svg?react";
import { useMandatorContext } from "@bn/theming";
import {
  ARIVA_HEADER_HEIGHT_DESKTOP,
  ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
} from "@components/ArivaHeader/arivaHeaderConfig";

const Container = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== "collapsed" && prop !== "headerOffset",
})<{ collapsed?: boolean; headerOffset?: number }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.palette.background.sidebar};
  top: ${({ headerOffset }) =>
    headerOffset !== undefined ? `${headerOffset}px` : "auto"};
  height: ${({ headerOffset }) =>
    headerOffset !== undefined ? `calc(100vh - ${headerOffset}px)` : "100vh"};
  transition:
    top 0.3s ease,
    height 0.3s ease;
  gap: ${({ theme }) => theme.spacing(4)};
  z-index: ${({ theme }) => theme.zIndex.drawer + 1};
  padding: ${({ theme, collapsed }) =>
    collapsed ? theme.spacing(4, 2, 5, 2) : theme.spacing(4, 4, 5, 4)};
`;

const InnerContainer = styled("div")`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  overflow-x: clip;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar: {
    display: none;
  }
`;

const ArrowContainer = styled(motion.div)`
  position: absolute;
  bottom: 105px;
  right: -11px;
  background-color: ${({
    theme: {
      palette: { sidebar },
    },
  }) => sidebar.sidebarBtnDefault};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: ${({ theme }) => theme.shadows[2]};
  color: ${({ theme }) => theme.palette.icon.secondary};
  cursor: pointer;
  z-index: 1;

  &:hover {
    background-color: ${({
      theme: {
        palette: { sidebar },
      },
    }) => sidebar.sidebarBtnHover};
    color: ${({ theme }) => theme.palette.button.primaryButton.textOnHover};
  }
`;

export interface SidebarProps {
  header: ReactNode;
  children: ReactNode;
  collapsed?: boolean;
  onCollapseClick?: () => void;
}

export const Sidebar = ({
  header,
  children,
  collapsed,
  onCollapseClick,
}: SidebarProps) => {
  const { isAriva } = useMandatorContext();
  // When the header hides on scroll, the sidebar extends up to the top.
  const headerHidden = useScrollTrigger({
    disableHysteresis: true,
    threshold: ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
  });
  const headerOffset = isAriva
    ? headerHidden
      ? 0
      : ARIVA_HEADER_HEIGHT_DESKTOP
    : undefined;
  const headerWithProps = isValidElement(header)
    ? cloneElement(header as ReactElement<{ collapsed?: boolean }>, {
        collapsed,
      })
    : header;

  return (
    <Container
      collapsed={collapsed}
      headerOffset={headerOffset}
      initial={{ width: collapsed ? 56 : 300 }}
      animate={{ width: collapsed ? 56 : 300 }}
    >
      <InnerContainer>
        {headerWithProps}
        {children}
      </InnerContainer>
      <ArrowContainer
        onClick={onCollapseClick}
        animate={{ rotate: collapsed ? 180 : 0, transition: { delay: 0.3 } }}
      >
        <ArrowLeft width="20px" height="20px" />
      </ArrowContainer>
    </Container>
  );
};
